import {
  AdminHeader,
  AdminSpinner,
  AdminSwitch,
  api,
  Box,
  BrandVDriveEntity,
  Button,
  DeliveryAssignmentNonOperatingConditionsEntity,
  DeliveryAssignmentProviderEntity,
  DeliveryAssignmentTimeEntity,
  Div,
  ErrorToast,
  getBrandVDriveById,
  getLocationVDriveById,
  H3,
  H5,
  Icon,
  ImproveWithAIButton,
  lightTheme,
  LocationVDriveEntity,
  P,
  PlusIcon,
  Separator,
  SubTitleWithTooltipIcon,
  SuccessToast,
  Tab,
  useVDrive,
  VDrivePreferredAssignmentType,
  VDriveProvider,
  VDriveStrategySection,
} from '@vgrubs/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AddAssignmentProvider } from './AddAssignmentProvider';
import { AddDeliveryProvider } from './AddDeliveryProvider';
import { AddNonOperating } from './AddNonOperating';
import {
  isApplicable,
  StrategyRouteState,
  suggestedFields,
} from './aiSuggestion/helpers/strategySuggestionFormat';
import { useStrategySuggestion } from './aiSuggestion/hooks/useStrategySuggestion';
import { StrategySuggestionModal } from './aiSuggestion/StrategySuggestionModal';
import { ApplyAssignmentToBrands } from './ApplyAssignmentToBrands';
import {
  DELIVERY_NOW_REASSIGN_MAX_MINUTES,
  DELIVERY_NOW_REASSIGN_MIN_MINUTES,
  DeliveryNowReassign,
} from './DeliveryNowReassign';
import {
  AssignmentNonOperatingCondition,
  BannedAssignmentDeliveryProvider,
  DeliveryAssignmentProvider,
  DeliveryAssignmentTimes,
} from './components';

export const AssignmentStrategy = () => {
  const {
    locationVDrive,
    setLocationVDrive,
    deliveryAssignment,
    setDeliveryAssignment,
    setApplyDeliveryAssignmentToAllBrands,
    brandVDrive,
    setBrandVDrive,
  } = useVDrive();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [addDeliveryProviderModal, setAddDeliveryProviderModal] = useState<
    boolean | { item: DeliveryAssignmentProviderEntity; index: number }
  >(false);

  const [editDeliveryProviderModal, setEditDeliveryProviderModal] = useState<
    boolean | { item: DeliveryAssignmentProviderEntity; index: number }
  >(false);

  const [applyToAllBrandsModal, setApplyToAllBrandsModal] = useState(false);

  const [preferredAssignmentType, setPreferredAssignmentType] = useState(
    VDrivePreferredAssignmentType.cheapest
  );
  const [providers, setProviders] = useState<
    DeliveryAssignmentProviderEntity[]
  >([]);

  const [nonOperatingConditions, setNonOperatingConditions] = useState<
    DeliveryAssignmentNonOperatingConditionsEntity[]
  >([]);

  const [editNonOperatingConditions, setEditNonOperatingConditions] = useState<
    | boolean
    | { item: DeliveryAssignmentNonOperatingConditionsEntity; index: number }
  >(false);

  const [bannedProviders, setBannedProviders] = useState<VDriveProvider[]>([]);

  const [addNonOperatingModal, setAddNonOperatingModal] = useState<
    | boolean
    | { item: DeliveryAssignmentNonOperatingConditionsEntity; index: number }
  >(false);
  const [addBannedProviderModal, setAddBannedProviderModal] = useState(false);

  const [deliveryAsapCollapse, setDeliveryAsapCollapse] = useState(false);
  const [ignoreGlobalStrategies, setIgnoreGlobalStrategies] = useState(false);
  const refDeliveryAsap = useRef<HTMLDivElement>(null);
  const elementHeightDeliveryAsap = useMemo(
    () => refDeliveryAsap?.current?.clientHeight,
    [deliveryAsapCollapse]
  );
  const refIgnoreGlobalStrategies = useRef<HTMLDivElement>(null);
  const elementHeightIgnoreGlobalStrategies = useMemo(
    () => refIgnoreGlobalStrategies?.current?.clientHeight,
    [ignoreGlobalStrategies]
  );
  const [availableProviders, setAvailableProviders] = useState<
    VDriveProvider[]
  >([]);

  const [times, setTimes] = useState<DeliveryAssignmentTimeEntity[]>([]);

  const [deliveryNowReassignEnabled, setDeliveryNowReassignEnabled] =
    useState(false);
  const [deliveryNowReassignMinutes, setDeliveryNowReassignMinutes] = useState<
    number | null
  >(null);

  const routerLocation = useLocation();
  const routeState = routerLocation.state as StrategyRouteState;
  const isBrand = routerLocation.pathname.includes('brand');

  const { id } = useParams() as { id: string };

  const suggestion = useStrategySuggestion({
    scope: isBrand ? 'brand' : 'location',
    id,
  });
  const [suggestionOpen, setSuggestionOpen] = useState(false);

  async function handleGetVDrive() {
    try {
      setLoading(true);
      const { data } = isBrand
        ? await getBrandVDriveById(id)
        : await getLocationVDriveById(id);
      if (isBrand) {
        setBrandVDrive(data.data as BrandVDriveEntity);
      } else {
        setLocationVDrive(data.data as LocationVDriveEntity);
      }
      setDeliveryAssignment(data?.data?.deliveryAssignment || null);
    } catch (error) {
      // reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (
      isBrand
        ? brandVDrive?.brand?.id !== Number(id)
        : locationVDrive?.location?.id !== Number(id)
    ) {
      handleGetVDrive();
    }
  }, [id, isBrand ? brandVDrive?.brand?.id : locationVDrive?.location?.id]);

  const handleConfirm = () => {
    if (
      deliveryNowReassignEnabled &&
      (!deliveryNowReassignMinutes ||
        deliveryNowReassignMinutes < DELIVERY_NOW_REASSIGN_MIN_MINUTES ||
        deliveryNowReassignMinutes > DELIVERY_NOW_REASSIGN_MAX_MINUTES)
    ) {
      ErrorToast(
        `Auto-reassign DeliveryNow minutes must be between ${DELIVERY_NOW_REASSIGN_MIN_MINUTES} and ${DELIVERY_NOW_REASSIGN_MAX_MINUTES}`
      );
      return;
    }

    if (isBrand) {
      handleConfirmApply();
    } else {
      setApplyToAllBrandsModal(true);
    }
  };

  const handleConfirmApplyToAllBrands = (v: boolean) => {
    setApplyDeliveryAssignmentToAllBrands(v);
    setApplyToAllBrandsModal(false);
    handleConfirmApply();
  };

  const handleConfirmApply = () => {
    setDeliveryAssignment({
      times,
      bannedProviders,
      nonOperatingConditions,
      preferredAssignmentType,
      providers,
      ignoreGlobalStrategies,
      deliveryNowReassignEnabled,
      deliveryNowReassignMinutes,
    });
    navigate(-1);
  };

  useEffect(() => {
    getAvailableProviders();
    setPreferredAssignmentType(
      deliveryAssignment?.preferredAssignmentType ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.preferredAssignmentType
          : locationVDrive?.deliveryAssignment?.preferredAssignmentType) ||
        VDrivePreferredAssignmentType.cheapest
    );
    setProviders(
      deliveryAssignment?.providers ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.providers
          : locationVDrive?.deliveryAssignment?.providers) ||
        []
    );
    setNonOperatingConditions(
      deliveryAssignment?.nonOperatingConditions ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.nonOperatingConditions
          : locationVDrive?.deliveryAssignment?.nonOperatingConditions) ||
        []
    );
    setBannedProviders(
      deliveryAssignment?.bannedProviders ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.bannedProviders
          : locationVDrive?.deliveryAssignment?.bannedProviders) ||
        []
    );
    setIgnoreGlobalStrategies(
      deliveryAssignment?.ignoreGlobalStrategies ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.ignoreGlobalStrategies
          : locationVDrive?.deliveryAssignment?.ignoreGlobalStrategies) ||
        false
    );
    setTimes(
      deliveryAssignment?.times ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.times
          : locationVDrive?.deliveryAssignment?.times) ||
        []
    );
    setDeliveryNowReassignEnabled(
      deliveryAssignment?.deliveryNowReassignEnabled ||
        (isBrand
          ? brandVDrive?.deliveryAssignment?.deliveryNowReassignEnabled
          : locationVDrive?.deliveryAssignment?.deliveryNowReassignEnabled) ||
        false
    );
    setDeliveryNowReassignMinutes(
      deliveryAssignment?.deliveryNowReassignMinutes ??
        (isBrand
          ? brandVDrive?.deliveryAssignment?.deliveryNowReassignMinutes
          : locationVDrive?.deliveryAssignment?.deliveryNowReassignMinutes) ??
        null
    );
  }, [locationVDrive, deliveryAssignment, brandVDrive]);

  /*
   * Fills the form only: the ticked sections overwrite their own fields and
   * leave the rest (including anything typed by hand) alone. Saving is still
   * Confirm here and then the vDrive page's save.
   */
  /*
   * Fills the form only: the ticked sections overwrite their own fields and
   * leave the rest (including anything typed by hand) alone. Saving is still
   * Confirm here and then the vDrive page's save.
   */
  const applySuggestedSections = (sections: VDriveStrategySection[]) => {
    const fields = suggestedFields(sections);
    if (fields.times) setTimes(fields.times);
    if (fields.bannedProviders) setBannedProviders(fields.bannedProviders);
    if (fields.nonOperatingConditions)
      setNonOperatingConditions(fields.nonOperatingConditions);
    if (fields.preferredAssignmentType)
      setPreferredAssignmentType(fields.preferredAssignmentType);
    if (fields.providers) setProviders(fields.providers);
    if (fields.ignoreGlobalStrategies !== undefined)
      setIgnoreGlobalStrategies(fields.ignoreGlobalStrategies);
    if (fields.deliveryNowReassignEnabled !== undefined)
      setDeliveryNowReassignEnabled(fields.deliveryNowReassignEnabled);
    if (fields.deliveryNowReassignMinutes !== undefined)
      setDeliveryNowReassignMinutes(fields.deliveryNowReassignMinutes);
    SuccessToast(
      `${sections.length} AI ${
        sections.length === 1 ? 'change' : 'changes'
      } applied. Review them and Confirm to keep them.`
    );
  };

  const handleApplySuggestion = (sections: VDriveStrategySection[]) => {
    applySuggestedSections(sections);
    setSuggestionOpen(false);
  };

  /*
   * Arriving from the dashboard's Worst Performing Locations with the
   * sections ticked there: the job is the same one (the id is kept per
   * location for the tab and its answer is cached), so they are applied here
   * once both it and this location's strategy have loaded. Declared after the
   * effect that fills the form from the strategy, so on the render both run
   * these setters come last and win. The keys are then dropped from the
   * history entry so a reload or a Back does not apply them again.
   */
  const pendingAiSections = routeState?.aiSections;
  const formReady =
    !loading &&
    (isBrand ? brandVDrive?.brand?.id : locationVDrive?.location?.id) ===
      Number(id);
  useEffect(() => {
    if (!pendingAiSections?.length || !formReady || !suggestion.result) return;
    applySuggestedSections(
      suggestion.result.sections.filter(
        (section) =>
          isApplicable(section) && pendingAiSections.includes(section.key)
      )
    );
    navigate(routerLocation.pathname, {
      replace: true,
      state: { ...routeState, aiSections: undefined },
    });
  }, [pendingAiSections, formReady, suggestion.result]);

  // The run outlives the modal, so the button says where it stands.
  const aiButtonLabel =
    suggestion.status === 'starting' || suggestion.status === 'pending'
      ? 'Analyzing...'
      : suggestion.status === 'done'
      ? 'View AI suggestion'
      : undefined;

  const ActionButtons = () => (
    <Div $flex $gap="16px" $columnSM $mtSM="20">
      {/* As wide as Confirm, so "View AI suggestion" fits on one line. */}
      <ImproveWithAIButton
        filled
        w="200px"
        label={aiButtonLabel}
        onClick={() => setSuggestionOpen(true)}
      />
      <Button $selected $w="200px" $wSM="100%" onClick={handleConfirm}>
        Confirm
      </Button>
    </Div>
  );

  const getAvailableProviders = async () => {
    const { data } = await api.get(`/v1/vdrive/strategies/available-providers`);
    setAvailableProviders(data.data.providers);
  };

  const handleAddBannedProvider = (provider: VDriveProvider) => {
    setBannedProviders((prev) => [...prev, provider]);

    setProviders((prev) => prev.filter((p) => p.provider !== provider));

    // When a provider is added to bannedProviders, remove it from all fallbackProviders in all providers
    setProviders((prev) =>
      prev.map((p) => ({
        ...p,
        fallbackProviders: p.fallbackProviders
          ? p.fallbackProviders.filter((fp) => fp !== provider)
          : p.fallbackProviders,
      }))
    );
  };

  const brandName = routeState?.name;

  const title = isBrand
    ? 'Brand Delivery Assignment Strategy'
    : 'Location Delivery Assignment Strategy';
  const name = isBrand ? brandName : locationVDrive?.location?.name;

  return (
    <>
      {loading && <AdminSpinner />}
      <AdminHeader title={title}>
        <Div $desktop>
          <ActionButtons />
        </Div>
      </AdminHeader>
      <Div $width="100%" $gap="50px" $gapSM="0" $alignCenter $flex $columnSM>
        <Div
          $flex
          $between
          $alignCenter
          $width="50%"
          $widthSM="100%"
          $m="32px 0"
          $mSM="20px 0"
        >
          <H3 $m="0">
            <span>{name}</span>
          </H3>
        </Div>

        <Div
          $flex
          $alignCenter
          $between
          $gap="16px"
          $columnSM
          $width="50%"
          $widthSM="100%"
          $m="32px 0"
          $mSM="20px 0"
          $startSM
        >
          <H5 $m="0">Preferred Assigning Option</H5>
          <Div $flex $gap="16px" $widthSM="100%">
            <Tab
              $width="170px"
              $widthSM="calc(50% - 8px)"
              $minWidthAutoSM
              onClick={() =>
                setPreferredAssignmentType(
                  VDrivePreferredAssignmentType.cheapest
                )
              }
              selected={
                preferredAssignmentType ===
                VDrivePreferredAssignmentType.cheapest
              }
            >
              Cheapest
            </Tab>
            <Tab
              $width="170px"
              $widthSM="calc(50% - 8px)"
              $minWidthAutoSM
              onClick={() =>
                setPreferredAssignmentType(
                  VDrivePreferredAssignmentType.fastest
                )
              }
              selected={
                preferredAssignmentType ===
                VDrivePreferredAssignmentType.fastest
              }
            >
              Fastest
            </Tab>
          </Div>
        </Div>
      </Div>

      <Div $width="100%" $flex $gap="50px" $gapSM="0" $columnSM>
        <Div $width="50%" $widthSM="100%" $column $flex $gap="16px">
          <Separator />
          <Div
            $flex
            $alignCenter
            $between
            $mb="12"
            $mt="12"
            $mbSM="0"
            $mtSM="0"
          >
            <SubTitleWithTooltipIcon
              mb="0"
              text={'Delivery Providers'}
              tooltip={`
                This section lets you choose which delivery fleets to use under specific conditions.<br/><br/>
                You can set:
                <ul style="margin: 8px 0; padding-left: 20px;">
                  <li>Distance (miles) – e.g. use one fleet up to 5 miles, another for longer distances.</li>
                  <li>Order provider – if an order comes from Uber Eats, you can assign it to DoorDash for delivery.</li>
                  <li>Timeframe – define certain hours of the day when this rule applies.</li>
                </ul>
                Each parameter is optional, but at least one must be provided.
                If the selected fleet is unavailable, the system will use your defined fallback fleets.
                If no fallback is set, the system automatically chooses the <b>Cheapest</b> or <b>Fastest</b> option (depending on your preference).
              `}
            />

            <Icon $pointer onClick={() => setAddDeliveryProviderModal(true)}>
              <PlusIcon />
            </Icon>
          </Div>
          {providers.length > 0 && (
            <Div $flex $column $gap="8px">
              {providers.map((item, i) => {
                return (
                  <DeliveryAssignmentProvider
                    key={i}
                    item={item}
                    index={i}
                    setProviders={setProviders}
                    availableProviders={availableProviders}
                    bannedProviders={bannedProviders}
                    setEditDeliveryProviderModal={setEditDeliveryProviderModal}
                  />
                );
              })}
            </Div>
          )}
          <Separator />
          <Div
            $flex
            $alignCenter
            $between
            $mb="12"
            $mt="12"
            $mbSM="0"
            $mtSM="0"
          >
            <SubTitleWithTooltipIcon
              mb="0"
              text={'Banned Delivery Providers'}
              tooltip={`
                Here you add fleets that you don’t want to be used for this location.
                <ul style="margin: 8px 0; padding-left: 20px;">
                  <li>They will be excluded from delivery setup.</li>
                  <li>They also cannot be selected in the Delivery Providers section.</li>
                </ul>

                If all providers are banned, the system will automatically use the <b>Cheapest</b> or <b>Fastest</b> option (depending on your preference).
              `}
            />

            <Icon $pointer onClick={() => setAddBannedProviderModal(true)}>
              <PlusIcon />
            </Icon>
          </Div>
          {bannedProviders.length > 0 && (
            <Div $flex $column $gap="8px">
              {bannedProviders.map((p, i) => (
                <BannedAssignmentDeliveryProvider
                  provider={p}
                  key={i}
                  setBannedProviders={setBannedProviders}
                  index={i}
                />
              ))}
            </Div>
          )}
          <Separator />
          <DeliveryNowReassign
            enabled={deliveryNowReassignEnabled}
            setEnabled={setDeliveryNowReassignEnabled}
            minutes={deliveryNowReassignMinutes}
            setMinutes={setDeliveryNowReassignMinutes}
          />
          <Separator />
          <H5 $m="0">Global Strategy</H5>
          <P $m="0">
            <b>Doordash</b> is banned in all <b>5 Borough</b> of New York City
            (The five boroughs of New York City are Manhattan, Brooklyn, Queens,
            the Bronx, and Staten Island)
          </P>
          <Box $flex $justifyCenter $between $column>
            <Div $flex $alignCenter $between $gap="16px">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'Ignore Global Strategy'}
                tooltip={`
                Turn this on if you want to ignore the global strategy for this location.
                `}
              />

              <AdminSwitch
                checked={ignoreGlobalStrategies}
                onChange={() => {
                  setIgnoreGlobalStrategies((prev) => !prev);
                }}
              />
            </Div>
          </Box>
          <Separator />
        </Div>
        <Div $width="50%" $widthSM="100%" $column $flex $gap="16px">
          <Separator $color={lightTheme.mediumGray} />

          <Div
            $flex
            $alignCenter
            $between
            $mb="12"
            $mt="12"
            $mbSM="0"
            $mtSM="0"
          >
            <SubTitleWithTooltipIcon
              mb="0"
              text={'vDrive Non-Operating Conditions If:'}
              tooltip={`
                This section defines when vDrive should not auto-assign drivers.<br/><br/>
                You can configure:
                <ul style="margin: 8px 0; padding-left: 20px;">
                  <li>Days of the week when auto-assignment should be disabled.</li>
                  <li>Timeframe (optional) – limit by part of the day.</li>
                  <li>Distance (miles) – e.g. prevent auto-assignment if the order is too far.</li>
                  <li>Order provider – block auto-assignment for orders coming from specific platforms.</li>
                </ul>
                Each parameter is optional, but at least one must be provided.
              `}
            />

            <Icon $pointer onClick={() => setAddNonOperatingModal(true)}>
              <PlusIcon />
            </Icon>
          </Div>
          {nonOperatingConditions.length > 0 && (
            <Div $flex $column $gap="8px">
              {nonOperatingConditions.map((item, i) => {
                return (
                  <AssignmentNonOperatingCondition
                    key={i}
                    item={item}
                    index={i}
                    setNonOperatingConditions={setNonOperatingConditions}
                    setEditNonOperatingConditions={
                      setEditNonOperatingConditions
                    }
                  />
                );
              })}
            </Div>
          )}

          <Separator />
          <DeliveryAssignmentTimes
            times={times}
            setTimes={setTimes}
            availableProviders={availableProviders}
          />
          <Separator />
        </Div>
      </Div>
      <Div $mobile>
        <ActionButtons />
      </Div>

      <AddDeliveryProvider
        isOpen={addDeliveryProviderModal}
        setIsOpen={setAddDeliveryProviderModal}
        setProviders={setProviders}
        availableProviders={availableProviders}
        bannedProviders={bannedProviders}
      />

      <AddDeliveryProvider
        isOpen={editDeliveryProviderModal}
        setIsOpen={setEditDeliveryProviderModal}
        setProviders={setProviders}
        availableProviders={availableProviders}
        bannedProviders={bannedProviders}
        edit
      />

      <AddNonOperating
        isOpen={addNonOperatingModal}
        setIsOpen={setAddNonOperatingModal}
        setProviders={setNonOperatingConditions}
      />

      <AddNonOperating
        isOpen={editNonOperatingConditions}
        setIsOpen={setEditNonOperatingConditions}
        setProviders={setNonOperatingConditions}
        edit
      />

      <AddAssignmentProvider
        isOpen={addBannedProviderModal}
        setIsOpen={setAddBannedProviderModal}
        addFunction={(provider) =>
          handleAddBannedProvider(provider as VDriveProvider)
        }
        header="Add Banned Delivery Provider"
        alreadyAddedProviders={bannedProviders}
        availableProviders={availableProviders}
      />
      <StrategySuggestionModal
        isOpen={suggestionOpen}
        closeModal={() => setSuggestionOpen(false)}
        scopeLabel={isBrand ? 'brand' : 'location'}
        status={suggestion.status}
        result={suggestion.result}
        errorMessage={suggestion.errorMessage}
        onStart={suggestion.start}
        onReset={suggestion.reset}
        onApply={handleApplySuggestion}
      />
      <ApplyAssignmentToBrands
        isOpen={applyToAllBrandsModal}
        setIsOpen={setApplyToAllBrandsModal}
        handleSubmit={handleConfirmApplyToAllBrands}
      />
    </>
  );
};
